import { Injectable, inject } from "@angular/core";
import { Observable, catchError, forkJoin, map, of } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { PaymentMethodApiService } from "../../services/generated/erp-api/services/payment-method-api.service";
import { DeliveryMethodApiService } from "../../services/generated/erp-api/services/delivery-method-api.service";
import { ProductApiService } from "../../services/generated/erp-api/services/product-api.service";
import { CompanyApiService } from "../../services/generated/waba-api/services/company-api.service";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";
import { DiningTableService } from "../catalog-storefront/dining-tables/dining-table.service";
import { SoldOutService } from "../product/sold-out/sold-out.service";
import { SalePointService } from "../sale-points/sale-point.service";
import { RestaurantFacts } from "./restaurant-health";

/** Reads the restaurant facts; each one that fails to load comes back null (no sentence, no warning). */
@Injectable({ providedIn: "root" })
export class RestaurantHealthService {
  private readonly productApi = inject(ProductApiService);
  private readonly salePoints = inject(SalePointService);
  private readonly deliveryMethodApi = inject(DeliveryMethodApiService);
  private readonly paymentMethodApi = inject(PaymentMethodApiService);
  private readonly diningTables = inject(DiningTableService);
  private readonly companyApi = inject(CompanyApiService);
  private readonly soldOut = inject(SoldOutService);

  load(): Observable<RestaurantFacts> {
    const headers = () => ({ "x-api-canal": "web", "x-api-requestId": uuidv4() });
    const safe = <T>(source: Observable<T>) => source.pipe(catchError(() => of(null)));
    return forkJoin({
      dishes: safe(
        this.productApi
          .searchProduct(
            { ...headers(), body: { page: 0, size: 1, filter: { archived: false, itemType: "PRODUCT" } } },
            SILENT_HTTP_CONTEXT,
          )
          .pipe(map((response) => response.body?.totalElements ?? response.body?.entities?.length ?? 0)),
      ),
      points: safe(this.salePoints.listWithMain().pipe(map((points) => points.filter((point) => point.active !== false)))),
      methods: safe(
        this.deliveryMethodApi.getAvailableDeliveryMethods(headers(), SILENT_HTTP_CONTEXT).pipe(map((response) => response.body ?? [])),
      ),
      paymentReady: safe(
        this.paymentMethodApi
          .getAvailablePaymentMethods(headers(), SILENT_HTTP_CONTEXT)
          .pipe(map((response) => (response.body ?? []).some((method) => method.active !== false))),
      ),
      tables: safe(this.diningTables.list().pipe(map((tables) => tables.length))),
      hours: safe(this.soldOut.workHours()),
      store: safe(this.companyApi.getStoreStatus(headers(), SILENT_HTTP_CONTEXT).pipe(map((response) => response.body ?? null))),
    }).pipe(
      map(({ points, methods, ...facts }) => ({
        ...facts,
        zonesReady: methods ? methods.some((method) => method.active !== false && !!method.zones?.length) : null,
        salePoints: points ? points.length : null,
        placedSalePoints: points
          ? points.filter((point) => typeof point.pickupLat === "number" && typeof point.pickupLng === "number" && !(point.pickupLat === 0 && point.pickupLng === 0)).length
          : null,
      })),
    );
  }

  reopen(): Observable<unknown> {
    return this.companyApi.updateStoreStatus(
      { "x-api-canal": "web", "x-api-requestId": uuidv4(), body: { closed: false } },
      SILENT_HTTP_CONTEXT,
    );
  }
}
